import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ControlClient, type PresenceState } from "./lib/control";
import { fetchIceServers } from "./lib/ice";
import { Rpc } from "./lib/rpc";
import { hasAuthToken } from "./lib/config";
import { Sidebar, type View, type SettingsPage } from "./components/Sidebar";
import { PLATFORMS } from "./lib/platforms";
import { LiveView } from "./components/LiveView";
import { AccountSwitcher } from "./components/AccountSwitcher";
import { HeaderBar } from "./components/HeaderBar";
import { StatusSubBar } from "./components/StatusSubBar";
import { JobsPage } from "./components/JobsPage";
import { ConfigPage } from "./components/ConfigPage";
import { PartnersPage } from "./components/PartnersPage";
import { ContentPage } from "./components/ContentPage";
import { CredentialsPage } from "./components/CredentialsPage";
import { CaptioningPage } from "./components/CaptioningPage";
import { QueuePage } from "./components/QueuePage";

export function App() {
  if (!hasAuthToken()) {
    return <AuthSetup />;
  }
  return <Dashboard />;
}

function AuthSetup() {
  const [token, setToken] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    const url = new URL(window.location.href);
    url.searchParams.set("token", token.trim());
    window.location.href = url.toString();
  }

  return (
    <div className="auth-setup">
      <div className="auth-card">
        <h1>fan-management</h1>
        <p className="auth-hint">
          Paste your <strong>ADMIN_TOKEN</strong> to authenticate this browser.
          It's stored locally — you only do this once per device.
        </p>
        <form onSubmit={submit}>
          <input
            type="password"
            placeholder="ADMIN_TOKEN…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoFocus
          />
          <button type="submit" disabled={!token.trim()}>Save</button>
        </form>
        <p className="auth-foot">
          The token came from your <code>npx wrangler secret put ADMIN_TOKEN</code> step.
        </p>
      </div>
    </div>
  );
}

function Dashboard() {
  const control = useMemo(() => new ControlClient(), []);
  const rpc = useMemo(() => new Rpc(control), [control]);
  const [iceServers, setIceServers] = useState<RTCIceServer[] | null>(null);
  const [controlConnected, setControlConnected] = useState(false);
  const [presence, setPresence] = useState<PresenceState | null>(null);
  const [view, setView] = useState<View>({ kind: "empty" });
  const [peerState, setPeerState] = useState<RTCPeerConnectionState | null>(null);
  const [loginStates, setLoginStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchIceServers().then(setIceServers);
    const offConn = control.onConnectionChange(setControlConnected);
    const offPres = control.onPresence(setPresence);
    control.connect();
    return () => { offConn(); offPres(); };
  }, [control]);

  const electronConnected = !!presence?.electronConnected;

  useEffect(() => {
    if (!electronConnected) { setLoginStates({}); return; }
    let cancelled = false;
    async function fetchStates() {
      try {
        const s = await rpc.call<Record<string, boolean>>("get-login-states");
        if (!cancelled) setLoginStates(s || {});
      } catch { /* swallow */ }
    }
    fetchStates();
    const offBatch = rpc.onEvent<Record<string, boolean>>("login-states-batch", (s) => setLoginStates(s || {}));
    const offAcct = rpc.onEvent("account-switched", () => fetchStates());
    return () => { cancelled = true; offBatch(); offAcct(); };
  }, [rpc, electronConnected]);

  // Sidebar greying for platforms paused-by-config on the active account.
  // Refetched on account-switch and whenever ConfigPage saves (via the
  // refreshDisabledByConfig callback prop passed below).
  const [disabledByConfig, setDisabledByConfig] = useState<Set<string>>(new Set());
  const refreshDisabledByConfig = useCallback(async () => {
    try {
      const accts = await rpc.call<{ activeAccountId: string | null }>("get-accounts");
      const activeId = accts?.activeAccountId;
      if (!activeId) { setDisabledByConfig(new Set()); return; }
      const res = await rpc.call<{ ok: boolean; data?: Record<string, { paused?: boolean }> }>(
        "get-account-promo", activeId,
      );
      const promo = (res && res.ok && res.data) || {};
      const next = new Set<string>();
      for (const [k, cfg] of Object.entries(promo)) {
        if (cfg && cfg.paused === true) next.add(k.toLowerCase());
      }
      setDisabledByConfig(next);
    } catch {
      // Non-critical — leave whatever was previously shown
    }
  }, [rpc]);
  useEffect(() => {
    if (!electronConnected) { setDisabledByConfig(new Set()); return; }
    refreshDisabledByConfig();
    const off = rpc.onEvent("account-switched", () => refreshDisabledByConfig());
    return off;
  }, [rpc, electronConnected, refreshDisabledByConfig]);

  // If electron drops, drop out of any view that depends on it.
  useEffect(() => {
    if (presence && !presence.electronConnected && view.kind !== "empty") {
      setView({ kind: "empty" });
    }
  }, [presence, view]);

  // Mirror the local Electron's active view: when we connect, ask which key
  // is currently active and switch to it. Subscribe to view-changed events
  // so subsequent local switches keep the dashboard in sync.
  const userInteracted = useRef(false);
  useEffect(() => {
    if (!electronConnected) return;
    let cancelled = false;
    async function fetchActive() {
      try {
        const r = await rpc.call<{ key: string | null }>("get-active-view");
        if (cancelled) return;
        // Race: Electron's WS hello fires before its createWindow() runs the
        // initial switchView('twitter'). If lastActiveKey is still null, fall
        // back to the first platform so the dashboard isn't stuck on empty.
        const key = r?.key || PLATFORMS[0]?.key || null;
        applyActiveKey(key);
      } catch { /* swallow */ }
    }
    function applyActiveKey(key: string | null) {
      if (!key) return;
      // Map the Electron's key into our View. Platform keys → live view,
      // supported settings keys → settings page, anything else → ignore.
      const isPlatform = PLATFORMS.some((p) => p.key === key);
      if (isPlatform) {
        setView({ kind: "platform", platform: key });
        return;
      }
      const supportedSettings: SettingsPage[] = ["content", "partners", "queue", "jobs", "config", "credentials", "captioning"];
      if ((supportedSettings as string[]).includes(key)) {
        setView({ kind: "settings", page: key as SettingsPage });
      }
    }
    // Only fetch initial active view if the user hasn't already navigated.
    // Mirror live updates always — local switches should propagate.
    if (!userInteracted.current) fetchActive();
    const off = rpc.onEvent<{ key: string | null }>("view-changed", (ev) => {
      // Don't fight the user — only mirror live changes if they haven't
      // explicitly picked a different view in this dashboard yet.
      if (userInteracted.current) return;
      applyActiveKey(ev?.key ?? null);
    });
    return () => { cancelled = true; off(); };
  }, [rpc, electronConnected]);

  function selectPlatform(platform: string) {
    userInteracted.current = true;
    if (view.kind === "platform" && view.platform === platform) {
      setView({ kind: "empty" });
    } else {
      setView({ kind: "platform", platform });
      control.send({ type: "view_switch", payload: { key: platform } });
    }
  }
  function selectSettings(page: SettingsPage) {
    userInteracted.current = true;
    control.send({ type: "view_switch", payload: { key: page } });
    if (view.kind === "settings" && view.page === page) {
      setView({ kind: "empty" });
    } else {
      setView({ kind: "settings", page });
    }
  }

  return (
    <div className="app">
      <Sidebar
        view={view}
        electronConnected={electronConnected}
        loginStates={loginStates}
        disabledByConfig={disabledByConfig}
        accountSwitcher={<AccountSwitcher rpc={rpc} electronConnected={electronConnected} />}
        onSelectPlatform={selectPlatform}
        onSelectSettings={selectSettings}
        rpc={rpc}
        controlConnected={controlConnected}
        presence={presence}
        peerState={peerState}
      />
      <main className="main">
        <section className="content">
          {view.kind === "platform" && (
            <>
              {/* Banner = HeaderBar + StatusSubBar stacked flush, mirroring
                  Electron's platform-header-bar + status-sub-bar. */}
              <div className="platform-banner">
                <HeaderBar rpc={rpc} electronConnected={electronConnected} platform={view.platform} />
                <StatusSubBar
                  rpc={rpc}
                  electronConnected={electronConnected}
                  platform={view.platform}
                  loggedIn={loginStates[view.platform]}
                />
              </div>
              {iceServers ? (
                <LiveView
                  control={control}
                  iceServers={iceServers}
                  platform={view.platform}
                  onPeerState={setPeerState}
                />
              ) : (
                <div className="empty">Fetching ICE servers…</div>
              )}
            </>
          )}
          {view.kind === "settings" && view.page === "jobs" && (
            <JobsPage rpc={rpc} electronConnected={electronConnected} />
          )}
          {view.kind === "settings" && view.page === "config" && (
            <ConfigPage
              rpc={rpc}
              electronConnected={electronConnected}
              onSaved={refreshDisabledByConfig}
            />
          )}
          {view.kind === "settings" && view.page === "partners" && (
            <PartnersPage rpc={rpc} electronConnected={electronConnected} />
          )}
          {view.kind === "settings" && view.page === "content" && (
            <ContentPage rpc={rpc} electronConnected={electronConnected} />
          )}
          {view.kind === "settings" && view.page === "credentials" && (
            <CredentialsPage rpc={rpc} electronConnected={electronConnected} />
          )}
          {view.kind === "settings" && view.page === "captioning" && (
            <CaptioningPage rpc={rpc} electronConnected={electronConnected} />
          )}
          {view.kind === "settings" && view.page === "queue" && (
            <QueuePage rpc={rpc} electronConnected={electronConnected} />
          )}
          {view.kind === "empty" && (
            <div className="empty">
              {!electronConnected
                ? "Start the Electron app on the worker PC to see live views."
                : "Pick a platform from the sidebar, or open Jobs / Config / Partners."}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
